import React from 'react';
import { TextV2, SpacingStack, ButtonV2 } from '@withjoy/joykit';
import { DrawerV2 } from '@withjoy/joykit/components/DrawerV2';
import { LockHeavy } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core/i18n';
import {
  styles,
  OutlineButton,
  StyledIconButton,
  StyledLinksContainer,
  StyledMenuLink,
  StyledMenuTitle,
  drawerOverrides,
  BottomStackStyled,
  StyledMenuLinkBox,
  StyledProfileButton,
  StyledLogoutButton,
} from './EventMenu.styles';
import BatchCard from './components/BatchCard/BatchCard';
import MenuButton from './components/MenuButton/MenuButton';
import { useEventMenuController } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/components/EventMenu/EventMenu.controller';
import { Link } from '@react-router';
import { customScrollbarCss } from '@shared/utils/style/scrollbar';
import { useTheme } from '@withjoy/joykit';
import chroma from 'chroma-js';
import { NavbarEventPropsInterface } from '../../../layout.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body2ToButtonsOverride,
  body2ToNavigationOverride,
  button2ToButtonsOverride,
  display3ToEventTitleOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { Category } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useSmartAppBannerProvider } from '@shared/components';

interface EventMenuProps
  extends Readonly<{
    title?: Maybe<string>;
    eventHandle: string;
    handleMenuOpenedChange?: (opened: boolean) => void;
    handleMenuClicked?: (pageTitle: string) => void;
    eventProps: NavbarEventPropsInterface;
    isCustomPage?: boolean;
    backgroundColor?: string;
  }> {}

const EventMenu: React.FC<EventMenuProps> = ({ title, eventHandle, handleMenuOpenedChange, handleMenuClicked, eventProps, isCustomPage, backgroundColor }) => {
  const {
    onOpen,
    isAdmin,
    isOpen,
    onClose,
    name,
    email,
    initials,
    unlockDialogHandle,
    rsvpHandle,
    pages,
    shouldDisplayRSVPButton,
    shouldDisplayUnlockButton,
    onProfileHandle,
    buttonNotYouText,
    logoutHandle,
    isProfile,
    handleClickItemMenu,
    unlockButtonText,
    rsvpButtonText,
  } = useEventMenuController({
    eventHandle,
    handleMenuClicked,
    handleMenuOpenedChange,
    eventProps,
  });
  const { t2 } = useTranslation('eventMenu');
  const editEventText = t2('editEvent');

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const eventPageRoute = useEventPageRoute(eventHandle);
  const isPreviewing = isInIframe();
  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const theme = useTheme();
  const linkTextColor = theme.colors.linkText;
  const colorLinkHoverBackground = chroma(linkTextColor).alpha(0.1).hex();
  const colorLinkActiveBackground = chroma(linkTextColor).alpha(0.2).hex();

  return (
    <>
      <MenuButton
        onClick={onOpen}
        isCustomPage={isCustomPage}
        topOffset={smartAppBannerHeight}
      />
      <DrawerV2
        anchor={'left'}
        disableAutoFocus
        overrides={drawerOverrides(smartAppBannerHeight)}
        isOpen={isOpen}
        onClose={onClose}
        useBackdrop={true}
      >
        <DrawerV2.CloseButton />
        <DrawerV2.Body __css={styles.drawerBody}>
          <SpacingStack
            css={customScrollbarCss}
            __css={styles.bodyStack}
            backgroundColor={backgroundColor}
          >
            <StyledLinksContainer __css={styles.linksContainer}>
              <GuestSiteTypographyOverride override={display3ToEventTitleOverride}>
                <StyledMenuTitle
                  {...styles.menuTitle}
                  typographyVariant="display3"
                  fontSize={scaleFontSize(pxToRem(36), Category.EVENT_TITLE)}
                >
                  {title}
                </StyledMenuTitle>
              </GuestSiteTypographyOverride>
              {pages
                ?.filter(page => page.pageSlug !== 'rsvp' && page.pageSlug !== 'guests')
                .map(page => (
                  <Link
                    onClick={() => handleClickItemMenu(page.pageSlug)}
                    style={styles.linkItem}
                    key={page.pageSlug}
                    to={eventPageRoute.goToPath(page.type, page.pageSlug)}
                  >
                    <StyledMenuLinkBox
                      hoverBackground={colorLinkHoverBackground}
                      activeBackground={colorLinkActiveBackground}
                    >
                      <GuestSiteTypographyOverride override={body2ToNavigationOverride}>
                        <StyledMenuLink {...styles.links}>{page.pageTitle}</StyledMenuLink>
                      </GuestSiteTypographyOverride>
                    </StyledMenuLinkBox>
                  </Link>
                ))}
            </StyledLinksContainer>
            <BottomStackStyled isPreviewing={isPreviewing}>
              {shouldDisplayUnlockButton && (
                <GuestSiteTypographyOverride override={body2ToButtonsOverride}>
                  <StyledIconButton
                    onClick={unlockDialogHandle}
                    intent="primary"
                    variant="outline"
                    startIcon={<LockHeavy />}
                  >
                    <TextV2>{unlockButtonText}</TextV2>
                  </StyledIconButton>
                </GuestSiteTypographyOverride>
              )}

              {shouldDisplayRSVPButton && (
                <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
                  <OutlineButton
                    onClick={rsvpHandle}
                    variant="outline"
                    intent="primary"
                    shape="rounded"
                  >
                    {rsvpButtonText}
                  </OutlineButton>
                </GuestSiteTypographyOverride>
              )}

              {isAdmin && (
                <ButtonV2
                  onClick={() => handleMenuClicked && handleMenuClicked('EditEvent')}
                  intent="primary"
                  size="sm"
                  typographyVariant="button1"
                  variant="outline"
                  as={Link}
                  to={`/${eventHandle}/edit/dashboard`}
                  shape="rounded"
                >
                  {editEventText}
                </ButtonV2>
              )}

              {initials && (
                <div>
                  <StyledProfileButton
                    as={!isProfile ? 'div' : undefined}
                    variant={'link'}
                    onClick={isProfile ? onProfileHandle : undefined}
                  >
                    <BatchCard
                      initials={`${initials}`}
                      name={`${name}`}
                      email={`${email}`}
                    />
                  </StyledProfileButton>
                  {!isAdmin && (
                    <StyledLogoutButton
                      variant={'link'}
                      typographyVariant={'label2'}
                      onClick={logoutHandle}
                    >
                      {buttonNotYouText}
                    </StyledLogoutButton>
                  )}
                </div>
              )}
            </BottomStackStyled>
          </SpacingStack>
        </DrawerV2.Body>
      </DrawerV2>
    </>
  );
};

export default EventMenu;
